import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { AlertCategory, AlertSummary, alertCategoriesTranslations } from "@/models/AlertModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type ActiveNotificationsProps = {
  patientId: string;
  className?: string;
};

export const AlertsList: React.FC<ActiveNotificationsProps> = ({ patientId, className }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const alerts = Queries.alerts.useListPatientAlerts(patientId);

  return (
    <Card className={className}>
      <CardHeader title={t("notifications.title")} />
      <CardContent className={styles.cardBody}>
        {alerts.length > 0 ? (
          <NotificationsList notifications={alerts} patientId={patientId} />
        ) : (
          <Typography variant="body" className={styles.noNotif}>
            {t("notifications.none")}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const NotificationsList = ({ notifications, patientId }: { notifications: AlertSummary[]; patientId: string }) => (
  <>
    {notifications.map((notification, idx) => (
      <Notification key={idx} notification={notification} patientId={patientId} />
    ))}
  </>
);

const Notification = ({ notification, patientId }: { notification: AlertSummary; patientId: string }) => {
  const styles = useStyles(makeStyles);
  const dismissAlert = Queries.alerts.useDismissAlert();

  return (
    <div className={styles.notifLink}>
      <Icons.alertTriangle className={styles.alertIcon} />
      <InfoNotification category={notification.category} />
      <Button
        variant="text"
        id={`notification-dismiss-${notification.id}`}
        data-action="common.notification.dismiss"
        data-notification={notification.id}
        className={cx(
          {
            [styles.disabledHoverButton]: !notification.canResolve,
          },
          "hover-button",
          styles.hoverButton,
        )}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          return (
            notification.canResolve &&
            dismissAlert.mutate({
              patientId: patientId,
              alertId: notification.id,
            })
          );
        }}
      >
        <Icons.check className={styles.icon} />
      </Button>
    </div>
  );
};

const InfoNotification = ({ category }: { category: AlertCategory }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.notifContent}>
      <Typography variant="bodySmall">{t(alertCategoriesTranslations[category])}</Typography>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  notifLink: css`
    display: flex;
    padding: ${theme.spacing(4)};
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(4)};
    border-radius: ${theme.spacing(1)};
    .hover-button {
      visibility: hidden;
      opacity: 0;
      cursor: pointer;
    }
    :hover {
      .hover-button {
        visibility: visible;
        opacity: 1;
        transition: opacity 0.3s;
      }
      background-color: ${theme.palette.background.default};
      transition: background-color 0.3s;
    }
  `,
  notifContent: css`
    display: flex;
    flex-direction: column;
    flex: 1 1;
    margin-left: ${theme.spacing(4)};
    justify-content: flex-start;
  `,
  noNotif: css`
    margin: ${theme.spacing(4)} 0;
    font-style: italic;
    color: ${theme.palette.text.secondary};
  `,
  cardBody: css`
    padding: ${theme.spacing(4)} ${theme.spacing(8)} ${theme.spacing(8)} ${theme.spacing(8)};
    display: flex;
    flex-direction: column;
  `,
  disabledHoverButton: css`
    visibility: hidden !important;
  `,
  hoverButton: css`
    background-color: ${theme.palette.primary.main};
    border-radius: 100%;
    height: fit-content;
    width: fit-content;
    min-width: 0;
    aspect-ratio: 1;
    padding: ${theme.spacing(4)};
    :hover {
      background-color: ${theme.palette.primary.light};
    }
  `,
  icon: css`
    color: ${theme.palette.common.white};
    width: ${FontSizes.bodyLarge};
    height: ${FontSizes.bodyLarge};
  `,
  alertIcon: css({
    width: FontSizes.bodyLarge,
    height: FontSizes.bodyLarge,
    fill: theme.palette.error.main,
  }),
});
